'use client';

import type { SubmitFormEvent } from '@hc-frontend/core-ui';
import { useEventBus, useTrackingContexts } from '@hc-frontend/core-ui';
import type { FilterGroupEntity } from '@hc-frontend/features-quotes';
import type { FormElement } from '@hc-frontend/third-party-snowplow';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import type { QuotesFiltersFormSubmitProps } from './quotes-filters-form-submit.types';

function buildFormElements({
  searchParams,
  filterGroups,
}: {
  searchParams: URLSearchParams;
  filterGroups: FilterGroupEntity[];
}) {
  const formElements: FormElement[] = [];
  const entries = Array.from(searchParams.entries());

  for (let i = 0; i < entries.length; i += 1) {
    const [key, value] = entries[i];
    const foundGroup = filterGroups.find((group) => group.key === key);
    const foundFilter = foundGroup?.items.find(
      (filter) => filter.value === value,
    );

    if (foundGroup && foundFilter) {
      formElements.push({
        name: foundGroup.key,
        value: foundFilter.label ?? foundFilter.value,
        nodeName: 'INPUT',
      });
    } else if (foundGroup) {
      formElements.push({
        name: foundGroup.key,
        value,
        nodeName: 'INPUT',
      });
    }
  }

  return formElements;
}

/**
 * Tracks all applied filters from url search params
 */
export function QuotesFiltersFormSubmit({
  formId,
  filterGroups,
}: QuotesFiltersFormSubmitProps) {
  const eventBus = useEventBus();
  const contexts = useTrackingContexts();
  const searchParams = useSearchParams();

  useEffect(() => {
    const formElements = buildFormElements({ searchParams, filterGroups });

    eventBus.publish<SubmitFormEvent, (EventTarget & HTMLFormElement) | null>({
      id: formId,
      topic: 'core.ui.form.submit',
      source: null,
      submitForm: {
        id: formId,
        elements: formElements,
      },
      contexts,
    });
  });

  return null;
}
